import { DataTable } from '@app/components/data-table/data-table';
import { useLocalDataTable } from '@app/components/data-table/hooks/use-local-data-table';
import { ProductProcessingType } from '@shared/api/types';
import { Button } from '@shared/components/ui/button';
import { useState } from 'react';
import ProductDetailProcessingSheet from '../processing/product-detail-processing-sheet';
import { useProductProcessingTableColumns } from './product-processing-table-columns';

const PAGE_SIZE = 10;

function ProcessingDataTable({
  data,
  maxEmissions,
  onRowClick,
}: {
  data: ProductProcessingType[];
  maxEmissions: number;
  onRowClick: (row: ProductProcessingType | undefined) => void;
}) {
  const columns = useProductProcessingTableColumns(maxEmissions);
  const { table } = useLocalDataTable({
    data,
    columns,
    getRowId: (row: ProductProcessingType) => row.name,
    initialState: { sorting: [{ id: 'emissions', desc: true }] },
  });
  return (
    <DataTable
      table={table}
      pagination={false}
      className="bg-gray-00 text-left shadow-sm"
      tableClassName="table-fixed"
      columnClassName={{ emissions: 'border-r', bar: 'pl-0 mr-0' }}
      onRowClick={(row) => onRowClick(row?.original)}
      rowClassName={() => 'cursor-pointer hover:bg-gray-50'}
    />
  );
}

interface ProductProcessingTableProps {
  processingTypes: ProductProcessingType[];
  maxEmissions: number;
  totalEmissions?: number;
  product?: string;
}

export default function ProductProcessingTable({
  processingTypes,
  maxEmissions,
  totalEmissions,
  product,
}: ProductProcessingTableProps) {
  const [showAll, setShowAll] = useState(false);
  const [selectedProcessingType, setSelectedProcessingType] =
    useState<ProductProcessingType>();

  const visibleTypes = showAll
    ? processingTypes
    : processingTypes.slice(0, PAGE_SIZE);

  return (
    <div>
      <ProcessingDataTable
        key={visibleTypes.length}
        data={visibleTypes}
        maxEmissions={maxEmissions}
        onRowClick={setSelectedProcessingType}
      />
      {processingTypes.length > PAGE_SIZE && (
        <Button
          variant="ghost"
          size="sm"
          className="text-muted-foreground"
          onClick={() => setShowAll((v) => !v)}
        >
          {showAll
            ? 'Show less'
            : `Show ${processingTypes.length - PAGE_SIZE} more`}
        </Button>
      )}
      {selectedProcessingType && totalEmissions != null && product && (
        <ProductDetailProcessingSheet
          product={product}
          isOpen={!!selectedProcessingType}
          setIsOpen={(open) => {
            if (!open) setSelectedProcessingType(undefined);
          }}
          processingType={selectedProcessingType}
          totalEmissions={totalEmissions}
        />
      )}
    </div>
  );
}
